import React from 'react';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, ShieldCheck, Hammer, Activity, Layers, Star, Droplets } from 'lucide-react';
import { DOMAIN, BRAND } from '../constants';
import { Reveal, SectionHero, TechCard } from '../components/UI';

export const Concrete: React.FC = () => {
  const pageTitle = "Béton Mobile Prêt à l'Emploi & Finition Béton Granby | Qualité Supérieure";
  const pageDesc = "Béton fabriqué sur mesure directement sur votre chantier. Payez seulement ce que vous utilisez. Finitions de béton : bouchardée, polie, teintée. Service à Granby.";
  const canonicalUrl = `${DOMAIN}/beton`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Béton Mobile sur Chantier",
    "provider": {
      "@type": "LocalBusiness",
      "name": BRAND
    },
    "areaServed": ["Granby", "Haute-Yamaska"],
    "description": "Fabrication de béton sur mesure directement sur le chantier avec bétonnière mobile. Payez au compteur.",
    "serviceType": "Concrete Construction"
  };

  return (
    <div className="bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1518709414768-a88986a18a9b?q=80&w=2070&auto=format&fit=crop" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1518709414768-a88986a18a9b?q=80&w=2070&auto=format&fit=crop" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <SectionHero 
        title="Le béton, un investissement à long terme" 
        subtitle="Performance & Durabilité" 
        image="https://images.unsplash.com/photo-1518709414768-a88986a18a9b?q=80&w=2070&auto=format&fit=crop"
        height="h-[60vh]"
      />
      
      <div className="container mx-auto px-6 py-20 bg-tech-grid">
        <div className="mb-20">
           <Reveal>
             <h2 className="text-4xl font-display font-bold text-slate-900 uppercase mb-12 flex items-center gap-4">
               <span className="w-12 h-2 bg-brand-red"></span>
               Les avantages du béton
             </h2>
           </Reveal>
           
           <div className="grid md:grid-cols-3 gap-6 mb-24">
             <Reveal delay={0}>
               <TechCard title="Investissement" icon={<TrendingUp />} number="01">
                 Un investissement très long terme. Rentabilité assurée par la longévité de l'ouvrage.
               </TechCard>
             </Reveal>
             <Reveal delay={100}>
               <TechCard title="Maintenance" icon={<ShieldCheck />} number="02">
                 Le béton est le matériau le plus simple à entretenir. Pas de traitements complexes requis.
               </TechCard>
             </Reveal>
             <Reveal delay={200}>
               <TechCard title="Résistance" icon={<Hammer />} number="03">
                 Le béton est l'un des matériaux les plus résistants disponibles sur le marché de la construction.
               </TechCard>
             </Reveal>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-16 mb-24">
           <Reveal>
             <div className="bg-white p-8 border border-slate-200 border-l-4 border-l-brand-red shadow-lg h-full">
                <h3 className="text-2xl font-display font-bold text-slate-900 mb-6 uppercase flex items-center gap-3">
                   <Activity className="text-brand-red" /> La dureté du béton
                </h3>
                <p className="text-slate-600 leading-relaxed text-justify mb-4">
                  Pour connaître la dureté du béton, nous effectuons des tests de rayage sur différentes zones de la dalle. En fonction du coulage de la dalle ou de son vieillissement, la dureté du béton peut varier d'un endroit à l'autre.
                </p>
                <div className="bg-slate-50 p-4 border border-slate-200">
                  <p className="font-mono text-sm text-slate-800">
                    <strong className="text-brand-red">NOTE TECHNIQUE :</strong> Ces tests de dureté sont effectués par rapport à l'échelle de Mohs.
                  </p>
                </div>
             </div>
           </Reveal>

           <Reveal delay={200}>
             <div className="bg-white p-8 border border-slate-200 border-l-4 border-l-slate-800 shadow-lg h-full">
                <h3 className="text-2xl font-display font-bold text-slate-900 mb-6 uppercase flex items-center gap-3">
                   <Layers className="text-brand-red" /> Le mélange du béton
                </h3>
                <p className="text-slate-600 leading-relaxed text-justify mb-4">
                  Notre béton est un matériau composite résultant du mélange d'un squelette granulaire (granulats) et d'une matrice pâteuse adhésive composée de ciment, d'eau et le plus souvent d'adjuvants, qui sont les constituants les plus fréquemment utilisés.
                </p>
                <p className="text-slate-600 leading-relaxed text-justify mb-4">
                   Notre mélange prêt à l'emploi est fait sur place, ce qui offre une qualité de produit des plus performantes sur le marché.
                </p>
                <p className="text-xs font-mono text-slate-500 uppercase tracking-wide">
                  *D'autres constituants peuvent être ajoutés pour obtenir des performances particulières.
                </p>
             </div>
           </Reveal>
        </div>

        <div className="bg-slate-900 text-white p-1 border-t-4 border-brand-red">
           <div className="border border-white/10 p-8 md:p-16 relative overflow-hidden bg-[url('https://www.transparenttextures.com/patterns/blueprint.png')]">
             <Reveal>
               <div className="flex justify-between items-end border-b border-white/20 pb-6 mb-12">
                 <div>
                   <h2 className="text-3xl md:text-4xl font-display font-bold uppercase tracking-widest">Les finitions offertes</h2>
                   <p className="text-brand-red font-mono text-xs mt-2">TECHNICAL_SURFACE_SPECS</p>
                 </div>
                 <Layers className="text-white/20" size={48} />
               </div>
               <div className="mb-16 bg-white/5 border border-white/20 p-6 flex flex-col md:flex-row items-center gap-6">
                 <div className="bg-brand-red p-4 rounded-full shrink-0">
                    <Star className="text-white" size={32} />
                 </div>
                 <div>
                    <h4 className="text-xl font-display font-bold uppercase text-white mb-2">La finition du béton</h4>
                    <p className="text-slate-300">
                      Nous offrons une flatteuse manuelle (Derby) pour lisser vous-même votre béton <strong className="text-white bg-brand-red px-2 py-0.5">gratuitement</strong>, ce qui vous occasionne une économie considérable.
                    </p>
                 </div>
               </div>
               <div className="space-y-16">
                 <div>
                   <h3 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-3">
                     <span className="w-2 h-8 bg-brand-red"></span> Dalle de béton bouchardée
                   </h3>
                   <div className="pl-5 border-l border-white/10">
                      <p className="text-slate-300 font-light text-lg mb-4">
                        Le béton est simplement poncé et non poli pour laisser au sol un aspect rugueux.
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <span className="px-3 py-1 bg-white/10 border border-white/20 text-xs font-mono uppercase">Propriétés antidérapantes</span>
                        <span className="px-3 py-1 bg-white/10 border border-white/20 text-xs font-mono uppercase">Aspect naturel</span>
                      </div>
                   </div>
                 </div>
                 <div>
                   <h3 className="text-2xl font-display font-bold text-white mb-4 flex items-center gap-3">
                     <span className="w-2 h-8 bg-brand-red"></span> Dalle de béton poncée
                   </h3>
                   <p className="text-brand-red font-mono text-xs uppercase mb-4 tracking-widest">Finition mate - satinée - brillante</p>
                   <p className="text-slate-300 mb-8 max-w-3xl">
                     Plus les phases de polissage du béton avec des segments à liant en résine sont nombreuses, plus le sol aura un aspect brillant. Plus la surface est brillante, plus la réflexion des rayons lumineux est importante.
                   </p>
                   <div className="grid md:grid-cols-3 gap-6">
                     <div className="bg-white/5 p-6 border border-white/10 hover:border-brand-red transition-colors group">
                        <h4 className="font-bold text-white uppercase text-sm tracking-widest mb-4 group-hover:text-brand-red">Finition Satinée</h4>
                        <p className="text-xs text-slate-400 font-mono mb-2">POUR LES INDUSTRIES</p>
                        <p className="text-slate-300 text-sm">En remplacement du sol en résine. Permet de réaliser une économie d'éclairage en évitant une réverbération trop importante.</p>
                     </div>
                     <div className="bg-white/5 p-6 border border-white/10 hover:border-brand-red transition-colors group">
                        <h4 className="font-bold text-white uppercase text-sm tracking-widest mb-4 group-hover:text-brand-red">Finition Mate</h4>
                        <p className="text-xs text-slate-400 font-mono mb-2">TERRASSE & PISCINE</p>
                        <p className="text-slate-300 text-sm">Évite la réverbération, l'aveuglement et les brûlures. <br/><em>Conseil :</em> Finition bouchardée pour les bords de piscine (anti-glissade).</p>
                     </div>
                     <div className="bg-white/5 p-6 border border-white/10 hover:border-brand-red transition-colors group">
                        <h4 className="font-bold text-white uppercase text-sm tracking-widest mb-4 group-hover:text-brand-red">Finition Brillante</h4>
                        <p className="text-xs text-slate-400 font-mono mb-2">INTÉRIEUR RÉSIDENTIEL</p>
                        <p className="text-slate-300 text-sm">Béton décoratif. Apporte un aspect design, créatif et original.</p>
                     </div>
                   </div>
                 </div>
                 <div className="mt-12 bg-gradient-to-r from-slate-800 to-transparent p-6 border-l-4 border-brand-red">
                    <h3 className="text-white font-display font-bold uppercase mb-2 flex items-center gap-2">
                      <Droplets size={20} className="text-brand-red"/> Possibilité de teinter le béton
                    </h3>
                    <p className="text-slate-400 text-sm font-mono">
                      Effet similaire au béton ciré, mais sans adjonction de produits chimiques.
                    </p>
                 </div>
               </div>
             </Reveal>
           </div>
        </div>
      </div>
    </div>
  );
};